import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { hasCopy } from 'features/accounts/TransactionDetails/transactionDetails.helpers';
import useTransferDetails from 'features/moveMoney/screens/transfer/hooks/useTransferDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface TransferListItemProps {
  primaryCopy?: string;
  secondaryCopy?: string;
  showsDivider: boolean;
}

const Separator = () => {
  return (
    <View style={styles.separatorWrapper}>
      <HairLineDivider />
    </View>
  );
};

const TransferListItem = ({
  primaryCopy = '',
  secondaryCopy = '',
  showsDivider = true,
}: TransferListItemProps) => {
  return (
    <>
      <ListItem
        primaryCopy={primaryCopy}
        secondaryCopy={secondaryCopy}
        lineFormat={ListItemLineFormat.LABEL}
        showsChevron={false}
      />
      {showsDivider ? <Separator /> : null}
    </>
  );
};

const TransferDetails = () => {
  const { transferId } = useLocalSearchParams<{ transferId: string }>();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferDetails',
  });
  const { transfer, fetching, error, refetchQuery } = useTransferDetails({
    transferId,
  });
  const { defaultTitle } = useDefaultErrorStrings();

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('headerTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.valueContainer}>
          <AmplitudeMask>
            <ExpressiveLgText>
              {transfer?.value.formattedWithPlusPrefixOrAbs ??
                transfer?.value.formatted}
            </ExpressiveLgText>
          </AmplitudeMask>
        </View>
        {transfer?.details?.map((group, index: number) => {
          const isLastGroup = index === transfer.details.length - 1;
          const groupKey = index.toString();
          return (
            <View key={groupKey}>
              {group?.items?.map((item, indexChild: number) => {
                const primaryCopy = hasCopy(item) ? item.copy : item?.value;
                const showsDivider = indexChild < group.items.length - 1;
                const key = `${groupKey}-${item.key}-${item.id}`;
                return (
                  <AmplitudeMask style={styles.amplitudeMask} key={key}>
                    <TransferListItem
                      primaryCopy={primaryCopy}
                      secondaryCopy={item.title}
                      showsDivider={showsDivider}
                    />
                  </AmplitudeMask>
                );
              })}
              {isLastGroup && group?.items?.length > 0 ? <Separator /> : null}
              {!isLastGroup ? <View style={styles.divider} /> : null}
            </View>
          );
        })}

        <TransferListItem
          primaryCopy={transfer?.disclaimer ?? ''}
          secondaryCopy={translatedText('arrives')}
          showsDivider={false}
        />
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={error.message ?? ''}
              tryAgainAction={refetchQuery}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  amplitudeMask: {
    flex: 1,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  separatorWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  valueContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.l,
  },
});

export default TransferDetails;
